import {Card, Col, Row, Spinner} from 'react-bootstrap'
import DataTable from '../../../../ui-kit/DataTable'
import {ListAgreementPendingReviewV2, GeneralPendingReviewV2} from './core/_models'
import DashboaradEmptyReviewCard from '../../../../_metronic/layout/components/dashboard-empty-review/DashboaradEmptyReviewCard'
import {ColumnDef, createColumnHelper} from '@tanstack/react-table'
import {useEffect, useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import {getAllInfoV2} from './core/_requests'
import {axiosPrivate} from '../../../libs/axios'

const DashboardPageV2 = ({canApprovePendingReview, ability}) => {
  const [includeZero, setIncludeZero] = useState(false)
  const [internalLoading, setInternalLoading] = useState(false)
  const [pendingAgreement, setPendingAgreement] = useState<ListAgreementPendingReviewV2[]>([])
  const [pendingLegalTina, setPendingLegalTina] = useState<GeneralPendingReviewV2[]>([])
  const [pendingHSDescription, setPendingHSDescription] = useState<GeneralPendingReviewV2[]>([])

  const columnHelper = createColumnHelper<ListAgreementPendingReviewV2>()
  const generalColumnHelper = createColumnHelper<GeneralPendingReviewV2>()

  const fetchAllInfoV2 = async () => {
    try {
      setInternalLoading(true)
      const response = await getAllInfoV2(axiosPrivate)
      const data = response.data.data
      setPendingAgreement(data.agreement_pending_review || [])
      setPendingLegalTina(data.legal_tina_pending_review || [])
      setPendingHSDescription(data.hs_description_pending_review || [])
      setInternalLoading(false)
    } catch (error) {
      console.error('Error fetching all info:', error)
    }
  }

  useEffect(() => {
    fetchAllInfoV2()
  }, [])

  const pendingAgreementColumns: ColumnDef<ListAgreementPendingReviewV2, any>[] = useMemo(
    () => [
      columnHelper.accessor('title', {
        header: 'Agreement',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor('id', {
        header: 'Review Scope',
        cell: (info) => {
          const type = info.row.original.type
          if (type === 'agreement') {
            return 'Agreement Detail'
          }

          return 'Agreement Content'
        },
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left',
        },
        enableSorting: false,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor('id', {
        header: 'Specific Item(s)',
        cell: (info) => {
          const type = info.row.original.type
          const status = info.row.original.origin_id != null ? 'Updated' : 'Created'
          const chapters = info.row.original.pending_chapters

          if (type === 'agreement') {
            return (
              <span>
                <span>{status}&nbsp;</span>
                <Link to={`/new-rta/review-agreement/${info.getValue()}`} className='text-primary'>
                  {info.row.original.title}
                </Link>
              </span>
            )
          }

          if (chapters && chapters.length > 0) {
            return (
              <div className='d-flex flex-column gap-2'>
                {chapters.map((chapter, index) => (
                  <span>
                    <span>{status}&nbsp;</span>
                    <Link
                      key={index}
                      to={`/new-rta/review-chapter/${chapter.id}`}
                      className='text-primary'
                    >
                      {chapter.title}
                    </Link>
                  </span>
                ))}
              </div>
            )
          }

          return 'Agreement Content'
        },
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left',
        },
        enableSorting: false,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor('id', {
        header: '',
        cell: (info) => {
          const reviewUrl =
            info.row.original.type == 'agreement' ? `/new-rta/` : `/new-rta/${info.getValue()}`
          return (
            <Link to={reviewUrl} className='link-primary'>
              <span className='fa fa-eye'></span>
            </Link>
          )
        },
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left',
        },
        enableSorting: false,
        enableGlobalFilter: false,
      }),
    ],
    [includeZero]
  )

  const pendingLegalTinaColumns: ColumnDef<GeneralPendingReviewV2, any>[] = useMemo(
    () => [
      generalColumnHelper.accessor('title', {
        header: 'Heading', // Legal TINA
        cell: (info) => {
          return info.getValue()
        },
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      generalColumnHelper.accessor('origin_id', {
        header: 'Status',
        cell: (info) => {
          return info.getValue() != null ? 'Updated' : 'Created'
        },
        meta: {
          headerClass: 'text-center',
          cellClass: 'text-center',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      generalColumnHelper.accessor('id', {
        header: 'Action',
        cell: (info) => {
          return (
            <Link
              to={`/legal-tina/review/${info.getValue()}`}
              className='link-primary'
            >
              <span className='fa fa-eye'></span>
            </Link>
          )
        },
        meta: {
          headerClass: 'text-center',
          cellClass: 'text-center',
        },
        enableSorting: false,
        enableGlobalFilter: false,
      }),
    ],
    [includeZero]
  )

  const pendingHSDescriptionColumns: ColumnDef<GeneralPendingReviewV2, any>[] = useMemo(
    () => [
      generalColumnHelper.accessor('title', {
        header: 'Description', // HS Description
        cell: (info) => {
          return info.getValue()
        },
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      generalColumnHelper.accessor('origin_id', {
        header: 'Status',
        cell: (info) => {
          return info.getValue() != null ? 'Updated' : 'Created'
        },
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      generalColumnHelper.accessor('id', {
        header: '',
        cell: (info) => {
          return (
            <Link
              to={`/master-data-hs-descriptions/review/${info.getValue()}`}
              className='link-primary'
            >
              <span className='fa fa-eye'></span>
            </Link>
          )
        },
        meta: {
          headerClass: 'text-center',
          cellClass: 'text-center',
        },
        enableSorting: false,
        enableGlobalFilter: false,
      }),
    ],
    [includeZero]
  )

  return (
    <div className='mt-4'>
      {/* if user can approve RTAs */}
      {ability.can('approve', 'PERM_APPROVE_AGREEMENT') &&
        (pendingAgreement.length > 0 || pendingAgreement.length > 0) && (
          <>
            <Row>
              <Col>
                <Card>
                  <Card.Header className='card-header-stretch'>
                    <Card.Title className='w-100'>
                      <div className='title-container w-100 d-flex flex-column justify-content-between'>
                        <div className='title d-flex align-items-center mb-3'>
                          <span className='fa fa-file-alt px-2'></span>
                          <h3 className='text-gray-700'>Agreements Requiring Review</h3>
                        </div>
                        <div className='subtitle text-gray-700'>
                          These agreements contain contents that have been edited by members and are
                          awaiting your review or approval.
                        </div>
                      </div>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body>
                    <DataTable<ListAgreementPendingReviewV2>
                      data={pendingAgreement}
                      columns={pendingAgreementColumns}
                      enablePagination={true}
                      enableSorting={true}
                      isLoading={internalLoading}
                      enableGlobalFilter={true}
                      globalFilterPlaceholder='Search Agreement Name'
                      defaultPageSize={10}
                      pageSizes={[5, 10, 20, 50, 100]}
                      className='my-custom-table-wrapper'
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

      {/* if user can approve Legal TINA */}
      {ability.can('approve', 'PERM_APPROVE_LEGAL_TINA') && pendingLegalTina.length > 0 && (
        <>
          <Row className='mt-4'>
            <Col>
              <Card>
                <Card.Header className='card-header-stretch'>
                  <Card.Title className='w-100'>
                    <div className='title-container w-100 d-flex flex-column justify-content-between'>
                      <div className='title d-flex align-items-center mb-3'>
                        <span className='fa fa-file-alt px-2'></span>
                        <h3 className='text-gray-700'>Legal TINA Requiring Review</h3>
                      </div>
                      <div className='subtitle text-gray-700'>
                        These legal TINAs contain contents that have been edited by members and are
                        awaiting your review or approval.
                      </div>
                    </div>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <DataTable<GeneralPendingReviewV2>
                    data={pendingLegalTina}
                    columns={pendingLegalTinaColumns}
                    enablePagination={true}
                    enableSorting={true}
                    isLoading={internalLoading}
                    enableGlobalFilter={true}
                    globalFilterPlaceholder='Search Legal TINA Heading'
                    defaultPageSize={10}
                    pageSizes={[5, 10, 20, 50, 100]}
                    className='my-custom-table-wrapper'
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* if user can approve HS Description */}
      {ability.can('approve', 'PERM_APPROVE_HS_DESCRIPTION') && pendingHSDescription.length > 0 && (
        <>
          <Row className='mt-4'>
            <Col>
              <Card>
                <Card.Header className='card-header-stretch'>
                  <Card.Title className='w-100'>
                    <div className='title-container w-100 d-flex flex-column justify-content-between'>
                      <div className='title d-flex align-items-center mb-3'>
                        <span className='fa fa-file-alt px-2'></span>
                        <h3 className='text-gray-700'>HS Descriptions Requiring Review</h3>
                      </div>
                      <div className='subtitle text-gray-700'>
                        These HS descriptions contain contents that have been edited by members and
                        are awaiting your review or approval.
                      </div>
                    </div>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <DataTable<GeneralPendingReviewV2>
                    data={pendingHSDescription}
                    columns={pendingHSDescriptionColumns}
                    enablePagination={true}
                    enableSorting={true}
                    isLoading={internalLoading}
                    enableGlobalFilter={true}
                    globalFilterPlaceholder='Search HS Description'
                    defaultPageSize={10}
                    pageSizes={[5, 10, 20, 50, 100]}
                    className='my-custom-table-wrapper'
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* if no pending reviews */}
      {pendingAgreement.length === 0 &&
        pendingLegalTina.length === 0 &&
        pendingHSDescription.length === 0 &&
        !internalLoading &&
        canApprovePendingReview && <DashboaradEmptyReviewCard />}
    </div>
  )
}

export default DashboardPageV2
